import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

const BASE = '/api/whatsapp-web';

export type WhatsappWebStatus =
  | 'disconnected'
  | 'starting'
  | 'qr'
  | 'authenticated'
  | 'ready'
  | 'auth_failure'
  | 'error';

export interface WhatsappWebState {
  status: WhatsappWebStatus;
  /** 'interakt' = official Business API; 'session' = linked phone (QR). */
  provider?: 'interakt' | 'session';
  /** Data URL of the QR image, present only while status === 'qr'. */
  qr: string | null;
  me: { number: string; pushname: string } | null;
  lastError: string;
  startedAt?: string | null;
  /** Business API inbox: 'test' = may message only the test numbers. */
  mode?: 'test' | 'live';
  testNumbers?: string[];
  canManage?: boolean;
  /** false = this user sees only their own clients' conversations. */
  seesAll?: boolean;
  /** false = not an inbox agent (and not Admin / Super Admin). */
  access?: boolean;
}

export interface WhatsappChat {
  id: string;
  name: string;
  phone?: string;
  isGroup: boolean;
  unreadCount: number;
  lastMessage: string;
  lastFromMe?: boolean;
  lastStatus?: string;
  timestamp: number | null;
  windowOpen?: boolean;
}

export interface WhatsappMessage {
  id: string;
  body: string;
  fromMe: boolean;
  author: string;
  timestamp: number | null;
  type?: string;
  hasMedia?: boolean;
  mediaUrl?: string | null;
  fileName?: string;
  templateName?: string;
  /** sent | delivered | read | failed | received */
  status?: string;
  error?: string;
  location?: { lat: number; lng: number; desc?: string } | null;
}

/** WhatsApp's 24-hour customer-service window for free-form messages. */
export interface ReplyWindow {
  open: boolean;
  closesAt: string | null;
  lastCustomerMessageAt: string | null;
}

export interface WhatsappContact {
  id: string;
  name: string;
  pushname: string;
  number: string;
  picUrl: string | null;
  isGroup: boolean;
  contactId?: string | null;
  email?: string;
  city?: string;
}

export interface WhatsappTemplate {
  name: string;
  language: string;
  category: string;
  headerFormat: string;
  headerText: string;
  sampleMediaUrl: string;
  sampleFileName: string;
  body: string;
  footer: string;
  buttons: string[];
  varCount: number;
}

export interface InboxSettings {
  mode: 'test' | 'live';
  testNumbers: string[];
  webhookUrl: string;
  webhookSecured: boolean;
  canChange?: boolean;
  /** CRM usernames allowed to use the inbox besides Admin / Super Admin. */
  agents: string[];
  staff: { username: string; name: string; role: string; department: string }[];
}

export const whatsappWebKeys = {
  root: ['whatsapp-web'] as const,
  status: ['whatsapp-web', 'status'] as const,
  chats: ['whatsapp-web', 'chats'] as const,
  messages: (chatId: string | null) => ['whatsapp-web', 'messages', chatId] as const,
  contact: (chatId: string | null) => ['whatsapp-web', 'contact', chatId] as const,
  templates: ['whatsapp-web', 'templates'] as const,
  settings: ['whatsapp-web', 'settings'] as const,
};

/** The server's own explanation of a failed request, else `fallback`. */
export function whatsappError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: string } } })?.response?.data;
  return data?.error || fallback;
}

/** Polls quickly while linking (QR rotates), slowly once connected. */
export function useWhatsappWebStatus() {
  return useQuery({
    queryKey: whatsappWebKeys.status,
    queryFn: async () => (await api.get<WhatsappWebState>(`${BASE}/status`)).data,
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === 'qr' || s === 'starting' || s === 'authenticated' ? 3_000 : 60_000;
    },
  });
}

export function useWhatsappChats(enabled: boolean) {
  return useQuery({
    queryKey: whatsappWebKeys.chats,
    queryFn: async () => (await api.get<{ chats: WhatsappChat[] }>(`${BASE}/chats`)).data.chats,
    enabled,
    refetchInterval: enabled ? 15_000 : false,
  });
}

export function useWhatsappMessages(chatId: string | null) {
  return useQuery({
    queryKey: whatsappWebKeys.messages(chatId),
    queryFn: async () =>
      (await api.get<{ messages: WhatsappMessage[]; window?: ReplyWindow }>(`${BASE}/chats/${encodeURIComponent(chatId!)}/messages`)).data,
    enabled: !!chatId,
    refetchInterval: chatId ? 8_000 : false,
    retry: false,
  });
}

export function useWhatsappContact(chatId: string | null) {
  return useQuery({
    queryKey: whatsappWebKeys.contact(chatId),
    queryFn: async () =>
      (await api.get<{ contact: WhatsappContact | null }>(`${BASE}/chats/${encodeURIComponent(chatId!)}/contact`)).data.contact,
    enabled: !!chatId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useWhatsappTemplates(enabled: boolean) {
  return useQuery({
    queryKey: whatsappWebKeys.templates,
    queryFn: async () => (await api.get<{ templates: WhatsappTemplate[] }>(`${BASE}/templates`)).data.templates,
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

export function useInboxSettings(enabled: boolean) {
  return useQuery({
    queryKey: whatsappWebKeys.settings,
    queryFn: async () => (await api.get<InboxSettings>(`${BASE}/settings`)).data,
    enabled,
  });
}

export function useWhatsappWebMutations() {
  const qc = useQueryClient();
  const refreshStatus = () => qc.invalidateQueries({ queryKey: whatsappWebKeys.status });
  const refreshChats = () => {
    qc.invalidateQueries({ queryKey: whatsappWebKeys.chats });
    qc.invalidateQueries({ queryKey: ['whatsapp-web', 'messages'] });
    qc.invalidateQueries({ queryKey: ['comm'] });
  };

  const connect = useMutation({
    mutationFn: async (options?: { restart?: boolean }) =>
      (await api.post<WhatsappWebState>(`${BASE}/connect`, options ?? {})).data,
    onSuccess: refreshStatus,
  });

  const disconnect = useMutation({
    mutationFn: async () => (await api.post<WhatsappWebState>(`${BASE}/disconnect`)).data,
    onSuccess: refreshStatus,
  });

  const send = useMutation({
    mutationFn: async (payload: { to: string; body: string }) =>
      (await api.post(`${BASE}/send`, payload)).data,
    onSuccess: refreshChats,
  });

  const sendMedia = useMutation({
    mutationFn: async (payload: { to: string; body: string; data: string; mimetype: string; filename: string }) =>
      (await api.post(`${BASE}/send-media`, payload)).data,
    onSuccess: refreshChats,
  });

  const sendTemplate = useMutation({
    mutationFn: async (payload: { to: string; templateName: string; values: string[] }) =>
      (await api.post(`${BASE}/send-template`, payload)).data,
    onSuccess: refreshChats,
  });

  const markRead = useMutation({
    mutationFn: async (chatId: string) => (await api.post(`${BASE}/chats/${encodeURIComponent(chatId)}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: whatsappWebKeys.chats }),
  });

  const saveSettings = useMutation({
    mutationFn: async (payload: { mode?: 'test' | 'live'; testNumbers?: string[]; newWebhookToken?: boolean; agents?: string[] }) =>
      (await api.put<InboxSettings>(`${BASE}/settings`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: whatsappWebKeys.settings });
      refreshStatus();
    },
  });

  return { connect, disconnect, send, sendMedia, sendTemplate, markRead, saveSettings, refreshChats };
}
